/**
 * Kern-Ports für Pakete OHNE `core`-Zugang (`operations`, `oauth-server`, `ai`) und Welle-2-Fakes
 * ([V] 4.3 `core-ports.ts`; R-7, R-35, R-36; Anhang A.core-ports). `apps/app` darf `core` direkt importieren
 * ([V] 4.2); deshalb sind diese Ports bewusst schlank.
 *
 * Sicherheitsfestlegungen:
 * - `CredentialStore.providerFor(ctx)` bindet den Provider an `ctx.userId`; ein Konto eines anderen
 *   Benutzers → `DomainFailure('NOT_FOUND')`, nie ein Token (R-35).
 * - `AuditSink.record` nimmt nur Ereignistypen einer `AuditEventRegistry` mit strengem Detail-Schema
 *   (nur UUID, Ganzzahl, Zahl, Wahrheitswert, ISO-Zeitpunkt, Aufzählung; kein freier `z.string()`, R-36).
 * - `PersonCommands.requestErasure` ist ein eigener Dashboard-Einstieg außerhalb der Freigabe-Richtlinie
 *   ([V] E-j Nr. 2a): ruft `approvalGate.decide` nicht auf; „nur Datenverantwortliche“ →
 *   `ACTION_DISABLED` mit `reason: 'data_steward_required'` (Route bildet auf 403 ab).
 * - `AuditSink.recordAnonymous` (C-C2, V12-17) schreibt Ereignisse ohne Benutzer unter denselben Regeln wie `record`.
 * - Fremdtexte sind `RawText` (Marken-Typ) und gelangen nur über `untrusted` in Ausgaben.
 */
import type { CredentialsProvider } from '@socialfy/connector-api';
import { z } from 'zod';
import { ACTION_KEYS } from './actions.js';
import { ApprovalConditions, ApprovalMode } from './approval.js';
import type {
  DataMode,
  DbTx,
  DomainContext,
  DomainResult,
  Page,
  PersonId,
  RawText,
  SystemContext,
  UserId,
} from './common.js';
import type { HeldLocks } from './locks.js';

/** Zulässige Werttypen in Audit-Details: nur IDs, Zahlen, Wahrheitswerte, Zeitpunkte, Aufzählungen (nie Freitext). */
export type AuditDetailKind = 'uuid' | 'int' | 'number' | 'boolean' | 'datetime' | 'enum';
export type AuditEventDefinition = {
  eventType: string;
  subjectType: string | null;
  details: z.ZodObject;
};
export interface AuditEventRegistry {
  /** wirft bei Freitextfeldern (z.string ohne enum/uuid/iso) oder doppeltem Typ */
  define(d: AuditEventDefinition): void;
  get(eventType: string): AuditEventDefinition | undefined;
}
/** `<bereich>.<ereignis>` in Kleinschrift, z. B. `approval_policy.changed`. */
export const AUDIT_EVENT_TYPE = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;

/**
 * Erstbestand der Registry in `contracts` ([MCP] 9.10, 12; Plan 6.3): Richtlinienänderungen. Details nur
 * Aufzählungen und Zahlen (kein Freitext, keine Zielgruppennamen): `action_key` aus `ACTION_KEYS`, Modi aus
 * `ApprovalMode` (`mode_old` ist beim ersten Anlegen `null`), Tageslimits `null` oder 1..500, geänderte
 * Bedingungen als Namen. Feldnamen nach [MCP] 12 (Plan-Fassung `old_mode` … weicht davon ab; die
 * Fachspezifikation ist die Quelle). Weitere Ereignistypen registrieren die Module selbst.
 */
const APPROVAL_POLICY_AUDIT_DETAILS = z.strictObject({
  action_key: z.enum(ACTION_KEYS),
  mode_old: ApprovalMode.nullable(),
  mode_new: ApprovalMode,
  daily_limit_old: z.int().min(1).max(500).nullable(),
  daily_limit_new: z.int().min(1).max(500).nullable(),
  conditions_changed: z.array(ApprovalConditions.keyof()).max(6),
});

export const INITIAL_AUDIT_EVENTS: readonly AuditEventDefinition[] = [
  {
    eventType: 'approval_policy.changed',
    subjectType: null,
    details: APPROVAL_POLICY_AUDIT_DETAILS,
  },
  {
    eventType: 'approval_policy.seeded',
    subjectType: null,
    details: APPROVAL_POLICY_AUDIT_DETAILS,
  },
  {
    eventType: 'approval_policy.reset',
    subjectType: null,
    details: APPROVAL_POLICY_AUDIT_DETAILS,
  },
];

export interface AuditSink {
  record(
    tx: DbTx,
    ctx: DomainContext | SystemContext,
    e: {
      eventType: string;
      subject?: { type: string; id: string };
      details: Record<string, unknown>;
    },
  ): Promise<void>;
  /**
   * C-C2 (V12-17): Ereignisse ohne Benutzer (z. B. `auth.denied`): `user_id` NULL, nur registrierte Detail-Schemas
   * (Regeln wie `record`), kein Subjekt. Implementierung: W1-19 (`operations/src/engine/audit/**`).
   */
  recordAnonymous(
    tx: DbTx,
    e: { eventType: string; requestId: string; details: Record<string, unknown> },
  ): Promise<void>;
}

export interface DataKeyring {
  activeKeyId(): string;
  encrypt(plaintext: Uint8Array, aad: Uint8Array): { keyId: string; ciphertext: Uint8Array };
  decrypt(c: { keyId: string; ciphertext: Uint8Array }, aad: Uint8Array): Uint8Array;
}

export interface CredentialStore {
  setToken(
    tx: DbTx,
    ctx: DomainContext,
    a: { connectedAccountId: string; token: string; issuedAt: Date | null; expiresAt: Date | null },
  ): Promise<{ generation: number }>;
  revoke(tx: DbTx, ctx: DomainContext, connectedAccountId: string): Promise<void>;
  providerFor(ctx: DomainContext | SystemContext): CredentialsProvider;
}

export interface SettingsStore {
  get(ctx: DomainContext, key: string): Promise<unknown>;
  set(
    ctx: DomainContext,
    key: string,
    value: unknown,
    actor: { userId: UserId; sessionId: string },
  ): Promise<void>;
}

export interface SessionService {
  create(a: {
    userId: UserId;
    authenticatedAt: Date;
    mode: DataMode;
  }): Promise<{ sessionId: string; cookieValue: string }>;
  get(
    cookieValue: string,
  ): Promise<{ sessionId: string; userId: UserId; mode: DataMode; authenticatedAt: Date } | null>;
  revoke(sessionId: string): Promise<void>;
  issueCsrfToken(sessionId: string): string;
  verifyCsrf(a: { sessionId: string; token: string; origin: string | null }): boolean;
}

export type PersonSummaryRaw = {
  personId: PersonId;
  displayName: RawText;
  position: RawText | null;
  organization: RawText | null;
  linkedinConnection: 'connected' | 'invited' | 'invited_by' | 'none' | 'unknown';
  strength: number | null;
  strengthCoverage: number;
  lastInteractionAt: string | null;
};

export interface NetworkQueries {
  overview(
    ctx: DomainContext,
    a: { periodDays: number },
  ): Promise<{
    connections: number;
    activeRelationships: number;
    conversationsActive: number;
    replyRate: number | null;
    trend: Record<string, number>;
  }>;
  searchPeople(
    ctx: DomainContext,
    a: {
      query?: string;
      organization?: string;
      minStrength?: number;
      connection?: 'connected' | 'invited' | 'invited_by' | 'none';
      audienceId?: string;
      cursor?: string;
      limit: number;
    },
  ): Promise<Page<PersonSummaryRaw>>;
  getPerson(
    ctx: DomainContext,
    personId: PersonId,
  ): Promise<{
    person: PersonSummaryRaw;
    strengthFactors: Record<string, number | null>;
    firstInteractionAt: string | null;
    profileUrl: string | null;
  } | null>;
  personTimeline(
    ctx: DomainContext,
    a: { personId: PersonId; since?: string; cursor?: string; limit: number },
  ): Promise<Page<{
    type: string;
    occurredAt: string;
    direction: 'in' | 'out' | 'none';
    objectRef: string | null;
    snippet: RawText | null;
    snippetOwn: boolean;
  }> | null>;
  listConversations(
    ctx: DomainContext,
    a: {
      personId?: PersonId;
      unansweredOnly: boolean;
      folder?: 'INBOX' | 'ARCHIVE';
      cursor?: string;
      limit: number;
    },
  ): Promise<
    Page<{
      conversationId: string;
      title: RawText | null;
      participants: { personId: PersonId; displayName: RawText }[];
      lastMessageAt: string;
      lastMessageDirection: 'in' | 'out';
      lastSnippet: RawText;
      lastSnippetOwn: boolean;
    }>
  >;
  getConversation(
    ctx: DomainContext,
    a: { conversationId: string; before?: string; limit: number },
  ): Promise<{
    conversationId: string;
    title: RawText | null;
    deepLink: string | null;
    hasMore: boolean;
    messages: {
      messageId: string;
      contentRevision: number;
      sentAt: string;
      author: 'self' | 'person' | 'unknown';
      authorPersonId: PersonId | null;
      text: RawText;
    }[];
  } | null>;
  personSummaries(
    ctx: DomainContext,
    personIds: PersonId[],
  ): Promise<Map<PersonId, PersonSummaryRaw>>;
}

/**
 * `list` (A-C1 a): **alle** offenen Prüfungen. `other` = bestätigte, sichtbare Person der Gegen-Identität,
 * sonst `null` (R3c, R4, R5: die Gegenseite ist nur eine URN-Identität ohne Person); `counterpart`
 * beschreibt die Gegen-Identität. Implementierung: W1-09 (V12-09).
 */
export interface IdentityReviewService {
  list(
    ctx: DomainContext,
    a: { cursor?: string; limit: number },
  ): Promise<
    Page<{
      reviewId: string;
      candidate: PersonSummaryRaw;
      other: PersonSummaryRaw | null;
      counterpart: {
        identityKind: 'member_urn' | 'profile_url';
        evidence: 'message_author' | 'invitation' | 'shared_identity';
        occurredAt: string | null;
      };
      reason: string;
    }>
  >;
  decide(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext,
    a: { reviewId: string; decision: 'same_person' | 'different_person'; note?: string },
  ): Promise<DomainResult<{ decisionId: string }>>;
}

export interface PersonCommands {
  merge(
    ctx: DomainContext,
    a: { sourceId: PersonId; targetId: PersonId },
  ): Promise<DomainResult<{ targetId: PersonId }>>;
  restrict(
    ctx: DomainContext,
    a: { personId: PersonId; reason: string },
  ): Promise<DomainResult<void>>;
  unrestrict(ctx: DomainContext, a: { personId: PersonId }): Promise<DomainResult<void>>;
  /** A-C1 d (V12-07): jede Löschung ist an eine nachweisbare Betroffenenanfrage gebunden (`privacyRequestId` Pflicht). */
  requestErasure(
    ctx: DomainContext,
    a: { personId: PersonId; privacyRequestId: string },
  ): Promise<DomainResult<{ operationId: string }>>;
}

/**
 * Transaktionsgebundener Löschanstoß (C-W2, Plan Welle 2 A VB-8 / RW-27; Anbieterpflicht W1-12a bzw. W1-30a
 * `createForErasure`, Bindung W3-03; Aufrufer TP2 beim Löschen einer Zielperson, deren Person nur wegen dieser Zielperson
 * existiert). Ergänzt `PersonCommands.requestErasure` (eigene Transaktion, Dashboard-Einstieg) um einen Einstieg **in der
 * Transaktion des Aufrufers**: `requestInTx` legt dort die Betroffenenanfrage (Grund `target_erased`, „Zielperson
 * gelöscht“), den Löschauftrag und die Markierung „Löschung ausstehend“ (`erasure_pending_at`, T6p) an. Ein Rollback des
 * Aufrufers nimmt alles zurück; ein flüchtiger Aufruf nach dem Commit (Verlust bei Absturz) entfällt.
 * - Sperren: `held` stammt aus `DomainLocks.acquire({ lifecycle: 'erase', personIds })` (`gateMode: 'exclusive'`, `person:<id>`);
 *   der Anbieter prüft das und fordert nie nach.
 * - idempotent je Person: ein laufender Löschauftrag → keine zweite Anfrage, kein zweiter Auftrag.
 * - fremde, unbekannte oder bereits gelöschte Person → `DomainFailure('NOT_FOUND')`.
 */
export interface PersonErasureRequester {
  requestInTx(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext | SystemContext,
    a: { personId: PersonId; reason: 'target_erased' },
  ): Promise<void>;
}

/**
 * Lesezugriff „ist Zielperson“ für `person_get.is_target` (C-E1, Plan Welle 2 E RW-E-1; Anbieter TP2 `targets`, W2-10).
 * `true` genau dann, wenn die Person **des Aufrufers** eine Zielperson im Zustand `active`, `paused`, `in_conversation`
 * oder `reached` ist (`archived`/`erasing` zählen nicht). Fremde, unbekannte, eingeschränkte und gelöschte Personen sind
 * ununterscheidbar `false`; der Datenmodus des Kontexts trennt Live- und Demo-Daten.
 */
export interface TargetMembershipQuery {
  isTarget(ctx: DomainContext, personId: PersonId): Promise<boolean>;
}

/**
 * Kontostatus für `socialfy_status` und das Dashboard (A-C1 e, V12-13). Implementierung W1-13
 * (`createAccountReads`); Statuswerte sind die Datenbankkonstanten aus `core.ts` (als Zeichenketten).
 */
export type AccountStatusView = {
  accounts: {
    accountId: string;
    connectorId: string;
    status: string;
    pausedReason: string | null;
    lastCompleteAt: string | null;
    streams: { stream: string; lastCompleteAt: string | null; lastRunStatus: string | null }[];
  }[];
  openGaps: { gapId: string; accountId: string; stream: string; kind: string; since: string }[];
};
export interface AccountStatusQueries {
  status(ctx: DomainContext): Promise<AccountStatusView>;
}

/** Auftragsvertrag W1-30a ↔ W1-31 (A-C1 f, V12-06, V12-33): Parameter der Aufträge `refresh` und `disconnect`. */
export const RefreshJobParams = z.strictObject({
  accountId: z.uuid(),
  streams: z.union([
    z.array(z.enum(['changelog', 'profile', 'connections', 'invitations', 'inbox'])).min(1),
    z.literal('all'),
  ]),
});
export const DisconnectJobParams = z.strictObject({
  accountId: z.uuid(),
  deleteData: z.boolean(),
});
